import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { taskAPI } from '../lib/api';
import { AlertTriangle, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Users, Filter, X } from 'lucide-react';

const STATUS_COLORS = {
  backlog: '#6b7280', todo: '#f59e0b', in_progress: '#3b82f6',
  ready_for_testing: '#8b5cf6', in_testing: '#8b5cf6',
  ready_for_delivery: '#0ea5e9', released: '#22c55e',
  done: '#10b981', cancelled: '#ef4444',
};

const STATUS_LABELS = {
  backlog: 'Backlog', todo: 'To Do', in_progress: 'In Progress',
  ready_for_testing: 'Ready for Testing', in_testing: 'In Testing',
  ready_for_delivery: 'Ready for Delivery', released: 'Released',
  done: 'Done', cancelled: 'Cancelled',
};

const PRIORITY_COLORS = {
  urgent: '#ef4444', high: '#f97316', medium: '#f59e0b', low: '#6b7280', none: '#94a3b8',
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase();
};

const DAY_MS = 86400000;

export default function ResourceTimeline({ onTaskClick, users: allUsers, projects = [], selectedProject: parentProject, filterStatus = [], filterAssignee = [] }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cellWidth, setCellWidth] = useState(36);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterSearch, setFilterSearch] = useState('');
  const scrollRef = useRef(null);
  const filterRef = useRef(null);

  useEffect(() => {
    fetchTimeline();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchTimeline = async () => {
    setLoading(true);
    try {
      const res = await taskAPI.getResourceTimeline();
      setTasks(res.data || []);
    } catch (e) { console.error('Timeline fetch error:', e); }
    finally { setLoading(false); }
  };

  // Group tasks by assignee (filtered by project, status, and assignee)
  const { assigneeRows, dateRange, totalDays, dateColumns } = useMemo(() => {
    let filtered = parentProject && parentProject !== 'all'
      ? tasks.filter(t => t.project?.id === parentProject || t.client?.id === parentProject)
      : tasks;
    // Apply parent status filter (multi-select array)
    if (filterStatus.length > 0) {
      filtered = filtered.filter(t => filterStatus.includes(t.status));
    }
    // Apply parent assignee filter (multi-select array)
    if (filterAssignee.length > 0) {
      filtered = filtered.filter(t =>
        (t.assignees || []).some(a => filterAssignee.includes(a.id || a))
      );
    }
    if (!filtered.length) return { assigneeRows: [], dateRange: null, totalDays: 0, dateColumns: [] };

    const byAssignee = {};
    let minDate = null, maxDate = null;

    filtered.forEach(t => {
      const sd = t.start_date?.substring(0, 10);
      const dd = t.due_date?.substring(0, 10);
      if (!sd || !dd) return;

      const start = new Date(sd);
      const end = new Date(dd);
      if (isNaN(start) || isNaN(end)) return;

      if (!minDate || start < minDate) minDate = new Date(start);
      if (!maxDate || end > maxDate) maxDate = new Date(end);

      (t.assignees || []).forEach(a => {
        if (!byAssignee[a.id]) byAssignee[a.id] = { id: a.id, name: a.name, tasks: [] };
        byAssignee[a.id].tasks.push({ ...t, _start: start, _end: end });
      });

      // Tasks without assignees go to "Unassigned"
      if (!t.assignees || t.assignees.length === 0) {
        if (!byAssignee['__unassigned']) byAssignee['__unassigned'] = { id: '__unassigned', name: 'Unassigned', tasks: [] };
        byAssignee['__unassigned'].tasks.push({ ...t, _start: start, _end: end });
      }
    });

    // Pad date range by 7 days each side
    if (minDate) minDate.setDate(minDate.getDate() - 7);
    if (maxDate) maxDate.setDate(maxDate.getDate() + 7);

    const totalDays = minDate && maxDate ? Math.ceil((maxDate - minDate) / DAY_MS) + 1 : 0;

    // Generate date columns
    const dateColumns = [];
    if (minDate) {
      for (let i = 0; i < totalDays; i++) {
        const d = new Date(minDate);
        d.setDate(d.getDate() + i);
        dateColumns.push(d);
      }
    }

    // Sort rows by name
    const rows = Object.values(byAssignee).sort((a, b) => a.name.localeCompare(b.name));

    // Detect overlaps per assignee and assign lanes
    rows.forEach(row => {
      row.tasks.sort((a, b) => a._start - b._start);
      const lanes = [];
      row.tasks.forEach(task => {
        let placed = false;
        for (let l = 0; l < lanes.length; l++) {
          const lastInLane = lanes[l][lanes[l].length - 1];
          if (task._start >= lastInLane._end) {
            lanes[l].push(task);
            task._lane = l;
            placed = true;
            break;
          }
        }
        if (!placed) {
          task._lane = lanes.length;
          lanes.push([task]);
        }
      });
      row._laneCount = Math.max(lanes.length, 1);
      // Mark overlaps
      row._hasOverlap = row._laneCount > 1;
      // Mark individual overlapping tasks
      row.tasks.forEach(task => {
        task._overlaps = row.tasks.some(other =>
          other.id !== task.id &&
          other._start < task._end && other._end > task._start
        );
      });
    });

    return { assigneeRows: rows, dateRange: { min: minDate, max: maxDate }, totalDays, dateColumns };
  }, [tasks, parentProject, filterStatus, filterAssignee]);

  const filteredRows = useMemo(() => {
    if (selectedAssignees.length === 0) return assigneeRows;
    return assigneeRows.filter(r => selectedAssignees.includes(r.id));
  }, [assigneeRows, selectedAssignees]);

  const filteredAssigneeOptions = useMemo(() => {
    if (!filterSearch) return assigneeRows;
    const q = filterSearch.toLowerCase();
    return assigneeRows.filter(r => r.name.toLowerCase().includes(q));
  }, [assigneeRows, filterSearch]);

  const scrollToToday = useCallback(() => {
    if (!scrollRef.current || !dateRange?.min) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dayOffset = Math.floor((today - dateRange.min) / DAY_MS);
    if (dayOffset >= 0) {
      scrollRef.current.scrollLeft = dayOffset * cellWidth - scrollRef.current.clientWidth / 3;
    }
  }, [dateRange, cellWidth]);

  useEffect(() => {
    if (!loading && dateRange) {
      setTimeout(scrollToToday, 100);
    }
  }, [loading, dateRange, scrollToToday]);

  const zoom = (dir) => {
    setCellWidth(prev => Math.max(18, Math.min(80, prev + dir * 8)));
  };

  const scrollDays = (dir) => {
    if (scrollRef.current) scrollRef.current.scrollLeft += dir * cellWidth * 7;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-6 h-6 border-2 border-[#002FA7] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!assigneeRows.length) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <Users size={40} className="text-slate-300" />
        <p className="text-slate-400 text-sm">No tasks with Start Date and Due Date found</p>
        <p className="text-slate-300 text-xs">Set dates on tasks to see them in the timeline</p>
      </div>
    );
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayOffset = dateRange?.min ? Math.floor((today - dateRange.min) / DAY_MS) : -1;
  const ROW_HEIGHT = 40;
  const HEADER_HEIGHT = 56;

  // Group dateColumns by month for header
  const months = [];
  let currentMonth = null;
  dateColumns.forEach((d, i) => {
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (key !== currentMonth) {
      months.push({ label: d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }), startIdx: i, span: 1 });
      currentMonth = key;
    } else {
      months[months.length - 1].span++;
    }
  });

  const overlapCount = filteredRows.filter(r => r._hasOverlap).length;

  return (
    <div className="flex flex-col h-full" data-testid="resource-timeline">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 py-2 border-b border-slate-200 bg-white flex-shrink-0 gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Resource Timeline</span>
          <span className="text-xs text-slate-400">{filteredRows.length} assignee{filteredRows.length !== 1 ? 's' : ''} · {tasks.length} tasks</span>
          {overlapCount > 0 && (
            <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200" data-testid="overlap-warning-badge">
              <AlertTriangle size={11} /> {overlapCount} with overlaps
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Assignee filter */}
          <div className="relative" ref={filterRef}>
            <button
              onClick={() => setFilterOpen(!filterOpen)}
              className={`flex items-center gap-1.5 h-7 px-2.5 text-xs font-medium rounded-md border transition-colors ${selectedAssignees.length > 0 ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
              data-testid="timeline-filter-btn">
              <Filter size={12} /> Assignees
              {selectedAssignees.length > 0 && <span className="bg-white/20 px-1 rounded-full text-[10px]">{selectedAssignees.length}</span>}
            </button>
            {filterOpen && (
              <div className="absolute right-0 top-full mt-1 w-60 bg-white border border-slate-200 rounded-lg shadow-lg z-50 overflow-hidden" data-testid="timeline-filter-dropdown">
                <div className="p-2 border-b border-slate-100">
                  <input type="text" placeholder="Search assignees..." value={filterSearch}
                    onChange={(e) => setFilterSearch(e.target.value)}
                    className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded outline-none focus:border-[#002FA7]"
                    data-testid="timeline-filter-search" />
                </div>
                <div className="max-h-48 overflow-y-auto p-1">
                  {filteredAssigneeOptions.map(r => (
                    <label key={r.id} className="flex items-center gap-2 px-2 py-1.5 text-xs text-slate-700 hover:bg-slate-50 rounded cursor-pointer">
                      <input type="checkbox" checked={selectedAssignees.includes(r.id)}
                        onChange={() => setSelectedAssignees(prev =>
                          prev.includes(r.id) ? prev.filter(x => x !== r.id) : [...prev, r.id]
                        )}
                        className="rounded border-slate-300 text-[#002FA7] focus:ring-[#002FA7]" />
                      <div className="w-5 h-5 rounded-full bg-[#002FA7]/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-[8px] font-bold text-[#002FA7]">{getInitials(r.name)}</span>
                      </div>
                      {r.name}
                      <span className="ml-auto text-[10px] text-slate-400">{r.tasks.length}</span>
                    </label>
                  ))}
                </div>
                {selectedAssignees.length > 0 && (
                  <div className="p-2 border-t border-slate-100">
                    <button onClick={() => setSelectedAssignees([])} className="text-xs text-red-500 hover:underline">Clear filters</button>
                  </div>
                )}
              </div>
            )}
          </div>

          <button onClick={() => scrollDays(-1)} className="h-7 w-7 flex items-center justify-center rounded border border-slate-200 text-slate-500 hover:bg-slate-50" title="Scroll left">
            <ChevronLeft size={14} />
          </button>
          <button onClick={scrollToToday} className="h-7 px-2.5 text-xs font-medium rounded border border-slate-200 text-slate-600 hover:bg-slate-50" data-testid="timeline-today-btn">
            Today
          </button>
          <button onClick={() => scrollDays(1)} className="h-7 w-7 flex items-center justify-center rounded border border-slate-200 text-slate-500 hover:bg-slate-50" title="Scroll right">
            <ChevronRight size={14} />
          </button>
          <div className="w-px h-5 bg-slate-200 mx-1" />
          <button onClick={() => zoom(-1)} className="h-7 w-7 flex items-center justify-center rounded border border-slate-200 text-slate-500 hover:bg-slate-50" title="Zoom out" data-testid="timeline-zoom-out">
            <ZoomOut size={13} />
          </button>
          <button onClick={() => zoom(1)} className="h-7 w-7 flex items-center justify-center rounded border border-slate-200 text-slate-500 hover:bg-slate-50" title="Zoom in" data-testid="timeline-zoom-in">
            <ZoomIn size={13} />
          </button>
        </div>
      </div>

      {/* Timeline Grid — single scroll container with sticky left column */}
      <div className="flex-1 overflow-auto" ref={scrollRef}>
        <div style={{ width: totalDays * cellWidth + 192, minWidth: '100%' }}>
          {/* Combined header row */}
          <div className="flex sticky top-0 z-20 bg-white">
            {/* Top-left corner (assignee header) */}
            <div className="sticky left-0 z-30 bg-white border-r border-b border-slate-200 flex items-end px-3 pb-2"
              style={{ minWidth: 192, width: 192, height: HEADER_HEIGHT }}>
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Assignee</span>
            </div>
            {/* Date header */}
            <div className="border-b border-slate-200" style={{ width: totalDays * cellWidth }}>
              {/* Month row */}
              <div className="flex" style={{ height: 22 }}>
                {months.map((m, i) => (
                  <div key={i} className="border-r border-slate-100 flex items-center justify-center text-[10px] font-semibold text-slate-500 bg-slate-50"
                    style={{ width: m.span * cellWidth }}>
                    {m.label}
                  </div>
                ))}
              </div>
              {/* Day row */}
              <div className="flex" style={{ height: HEADER_HEIGHT - 22 }}>
                {dateColumns.map((d, i) => {
                  const isToday = d.getTime() === today.getTime();
                  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                  const showLabel = cellWidth >= 28 || d.getDate() === 1 || d.getDay() === 1;
                  return (
                    <div key={i} className={`border-r flex flex-col items-center justify-center ${isToday ? 'bg-[#002FA7]/10 border-[#002FA7]/30' : isWeekend ? 'bg-slate-50/80 border-slate-100' : 'border-slate-100'}`}
                      style={{ width: cellWidth, minWidth: cellWidth }}>
                      {showLabel && (
                        <>
                          <span className={`text-[9px] ${isToday ? 'text-[#002FA7] font-bold' : isWeekend ? 'text-slate-300' : 'text-slate-400'}`}>
                            {d.toLocaleDateString('en-US', { weekday: 'narrow' })}
                          </span>
                          <span className={`text-[10px] font-medium ${isToday ? 'text-[#002FA7] bg-[#002FA7]/20 w-5 h-5 rounded-full flex items-center justify-center' : isWeekend ? 'text-slate-300' : 'text-slate-500'}`}>
                            {d.getDate()}
                          </span>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Data rows — assignee + task bars in same scroll */}
          {filteredRows.map(row => (
            <div key={row.id} className="flex" style={{ height: row._laneCount * ROW_HEIGHT }}>
              {/* Sticky assignee cell */}
              <div className={`sticky left-0 z-10 flex items-center gap-2 px-3 border-r border-b border-slate-100 bg-white ${row._hasOverlap ? 'bg-amber-50/50' : ''}`}
                style={{ minWidth: 192, width: 192 }}
                data-testid={`timeline-assignee-${row.id}`}>
                <div className="w-6 h-6 rounded-full bg-[#002FA7]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[9px] font-bold text-[#002FA7]">{getInitials(row.name)}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-xs font-medium text-slate-700 truncate block">{row.name}</span>
                  <span className="text-[10px] text-slate-400">{row.tasks.length} task{row.tasks.length !== 1 ? 's' : ''}</span>
                </div>
                {row._hasOverlap && (
                  <AlertTriangle size={12} className="text-amber-500 flex-shrink-0" title="Has overlapping tasks" />
                )}
              </div>
              {/* Task bar area */}
              <div className={`relative border-b border-slate-100 ${row._hasOverlap ? 'bg-amber-50/30' : ''}`}
                style={{ width: totalDays * cellWidth, height: row._laneCount * ROW_HEIGHT }}>
                {/* Background grid */}
                <div className="absolute inset-0 flex pointer-events-none">
                  {dateColumns.map((d, i) => {
                    const isToday = d.getTime() === today.getTime();
                    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                    return (
                      <div key={i} className={`border-r ${isToday ? 'bg-[#002FA7]/5 border-[#002FA7]/20' : isWeekend ? 'bg-slate-50/60 border-slate-50' : 'border-slate-50'}`}
                        style={{ width: cellWidth, minWidth: cellWidth }} />
                    );
                  })}
                </div>
                {/* Today line */}
                {todayOffset >= 0 && todayOffset < totalDays && (
                  <div className="absolute top-0 bottom-0 w-0.5 bg-[#002FA7] z-[6] pointer-events-none"
                    style={{ left: todayOffset * cellWidth + cellWidth / 2 }} />
                )}
                {/* Task bars */}
                {row.tasks.map(task => {
                  const startOffset = Math.floor((task._start - dateRange.min) / DAY_MS);
                  const duration = Math.max(Math.ceil((task._end - task._start) / DAY_MS) + 1, 1);
                  const left = startOffset * cellWidth;
                  const width = duration * cellWidth - 4;
                  const top = (task._lane || 0) * ROW_HEIGHT + 6;
                  const barHeight = ROW_HEIGHT - 12;
                  const statusColor = STATUS_COLORS[task.status] || '#6b7280';
                  const priorityColor = PRIORITY_COLORS[task.priority] || '#94a3b8';

                  return (
                    <div key={task.id}
                      className="absolute rounded-md cursor-pointer transition-shadow hover:shadow-md group"
                      style={{
                        left, top, width: Math.max(width, 20), height: barHeight,
                        background: `linear-gradient(135deg, ${statusColor}e6, ${statusColor}cc)`,
                        border: task._overlaps ? `2px solid #f59e0b` : `1px solid ${statusColor}`,
                        zIndex: 5,
                      }}
                      title={`${task.task_number}: ${task.title}\n${task.start_date?.substring(0, 10)} → ${task.due_date?.substring(0, 10)}\nStatus: ${STATUS_LABELS[task.status] || task.status}${task._overlaps ? '\n⚠ Overlapping schedule' : ''}`}
                      onClick={() => onTaskClick && onTaskClick(task)}
                      data-testid={`timeline-bar-${task.id}`}>
                      {/* Priority indicator */}
                      <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-md" style={{ background: priorityColor }} />
                      {/* Label */}
                      {width > 60 && (
                        <div className="px-2 flex items-center h-full overflow-hidden">
                          <span className="text-[10px] font-semibold text-white truncate drop-shadow-sm">
                            {task.task_number} {width > 120 ? `· ${task.title}` : ''}
                          </span>
                        </div>
                      )}
                      {/* Overlap warning badge */}
                      {task._overlaps && (
                        <div className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-amber-400 border-2 border-white flex items-center justify-center z-10"
                          data-testid={`overlap-badge-${task.id}`}>
                          <AlertTriangle size={8} className="text-white" />
                        </div>
                      )}
                      {/* Tooltip on hover */}
                      <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block z-50 pointer-events-none">
                        <div className="bg-slate-900 text-white rounded-md px-3 py-2 text-xs shadow-lg whitespace-nowrap max-w-xs">
                          <div className="font-semibold">{task.task_number}: {task.title?.substring(0, 50)}{task.title?.length > 50 ? '...' : ''}</div>
                          <div className="text-slate-300 mt-0.5">{task.start_date?.substring(0, 10)} → {task.due_date?.substring(0, 10)}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: statusColor, color: '#fff' }}>
                              {STATUS_LABELS[task.status] || task.status}
                            </span>
                            {task._overlaps && <span className="text-amber-400 font-medium">⚠ Overlap</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
